import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Loader2, Info, PiggyBank } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

const TIERS = [
  { key: "donate_50", amount: 50 },
  { key: "donate_100", amount: 100 },
  { key: "donate_300", amount: 300 },
  { key: "donate_500", amount: 500 },
  { key: "donate_1000", amount: 1000 },
];

// Toronto Contribution Rebate Program formula:
// - Donations of $25 or less: no rebate
// - 75% of the first $300
// - 50% of the amount above $300
function calcRebate(amount) {
  if (!amount || amount <= 25) return 0;
  if (amount <= 300) return +(amount * 0.75).toFixed(2);
  return +(225 + (amount - 300) * 0.5).toFixed(2);
}

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export const Donate = () => {
  const { t } = useLang();
  const [selected, setSelected] = useState("donate_100");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ total_cents: 0, goal_dollars: 0, count: 0 });

  useEffect(() => {
    api.get("/donations/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const pickTier = (key) => { setSelected(key); setCustom(""); };
  const onCustom = (e) => { setCustom(e.target.value); setSelected(null); };

  const raised = progress.total_cents / 100;
  const goal = progress.goal_dollars || 1;
  const pct = Math.min(100, Math.round((raised / goal) * 100));

  const activeAmount = custom
    ? parseFloat(custom) || 0
    : (TIERS.find((tt) => tt.key === selected)?.amount || 0);
  const activeRebate = calcRebate(activeAmount);
  const activeNet = activeAmount - activeRebate;

  const donate = async () => {
    setLoading(true);
    try {
      const payload = { origin_url: window.location.origin, donor_name: name, donor_email: email };
      if (custom) {
        const val = Math.round(parseFloat(custom) * 100);
        if (!val || val < 500) { toast.error(t.donate.pick); setLoading(false); return; }
        payload.custom_amount = val;
      } else if (selected) {
        payload.lookup_key = selected;
      } else {
        toast.error(t.donate.pick); setLoading(false); return;
      }
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const money = (n) => `$${Math.round(n).toLocaleString()}`;
  const inputCls = "w-full bg-transparent border border-white/20 rounded-sm px-4 py-3 text-white placeholder-white/35 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-colors";

  return (
    <section id="donate" data-testid="donate-section" className="py-24 md:py-40 relative overflow-hidden">
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(147,51,234,0.25), transparent 70%)" }} />
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative">
        <div className="text-center mb-12">
          <Reveal><Overline>{t.donate.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.85] mt-4">{t.donate.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto font-light">{t.donate.lead}</p>
          </Reveal>
        </div>

        {/* Fundraising thermometer */}
        <Reveal delay={0.15}>
          <div className="mb-10" data-testid="donation-thermometer">
            <div className="flex items-end justify-between mb-3">
              <div>
                <span className="font-display text-4xl md:text-5xl text-purple-400" data-testid="raised-amount">{money(raised)}</span>
                <span className="text-white/50 text-sm ml-2 uppercase tracking-wider">{t.donate.raised}</span>
              </div>
              <div className="text-right text-white/50 text-sm uppercase tracking-wider">
                {t.donate.goalLabel}: <span className="text-white/80">{money(goal)}</span>
              </div>
            </div>
            <div className="h-4 rounded-full bg-white/10 overflow-hidden border border-white/10">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${pct}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-400"
                data-testid="thermometer-fill"
              />
            </div>
            <div className="mt-2 text-xs text-white/40 uppercase tracking-wider">
              {pct}% · {progress.count} {t.donate.backers}
            </div>
          </div>
        </Reveal>

        {/* Rebate info banner */}
        <Reveal delay={0.2}>
          <div
            data-testid="rebate-banner"
            className="mb-8 rounded-2xl border border-purple-500/30 bg-purple-500/5 p-5 md:p-6 flex gap-4"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-purple-600/25 border border-purple-500/40 flex items-center justify-center text-purple-300">
              <PiggyBank size={20} />
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-purple-300">{t.donate.rebateTitle}</div>
              <p className="text-white/75 mt-2 text-sm md:text-base leading-relaxed">{t.donate.rebateBody}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="glass rounded-2xl p-6 md:p-10 purple-glow">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {TIERS.map((tier) => {
                const reb = calcRebate(tier.amount);
                const net = tier.amount - reb;
                const isActive = selected === tier.key;
                return (
                  <button
                    key={tier.key}
                    data-testid={`donate-tier-${tier.amount}`}
                    onClick={() => pickTier(tier.key)}
                    className={`relative py-5 px-3 rounded-xl border text-left transition-colors ${
                      isActive
                        ? "bg-purple-600 border-purple-600 text-white"
                        : "border-white/15 text-white/80 hover:border-purple-500"
                    }`}
                  >
                    <div className="font-display text-3xl md:text-4xl leading-none text-center">${tier.amount}</div>
                    <div className={`mt-3 text-[10px] uppercase tracking-wider text-center ${isActive ? "text-purple-100" : "text-white/45"}`}>
                      {t.donate.finalCost}
                    </div>
                    <div className={`text-center font-semibold text-lg ${isActive ? "text-white" : "text-purple-300"}`}>
                      {fmt(net)}
                    </div>
                    <div className={`text-center text-[10px] mt-1 ${isActive ? "text-purple-100" : "text-white/40"}`}>
                      −{fmt(reb)} {t.donate.rebate}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <label className="text-sm text-white/60 uppercase tracking-wider">{t.donate.custom}</label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg">$</span>
                <input
                  type="number"
                  min="5"
                  data-testid="donate-custom"
                  value={custom}
                  onChange={onCustom}
                  placeholder={t.donate.customPlaceholder}
                  className="w-full bg-transparent border border-white/20 rounded-sm pl-9 pr-4 py-4 text-white text-lg placeholder-white/35 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-colors"
                />
              </div>
            </div>

            {/* Live rebate summary */}
            {activeAmount > 0 && (
              <div data-testid="rebate-summary" className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/45">{t.donate.give}</div>
                  <div className="font-display text-2xl mt-1">{fmt(activeAmount)}</div>
                </div>
                <div className="rounded-lg border border-purple-500/40 bg-purple-500/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-purple-300">{t.donate.youGetBack}</div>
                  <div className="font-display text-2xl mt-1 text-purple-300" data-testid="rebate-value">{fmt(activeRebate)}</div>
                </div>
                <div className="rounded-lg border border-white/20 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/45">{t.donate.finalCost}</div>
                  <div className="font-display text-2xl mt-1" data-testid="net-value">{fmt(activeNet)}</div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <input data-testid="donor-name" className={inputCls} placeholder={t.donate.donorName} value={name} onChange={(e) => setName(e.target.value)} />
              <input type="email" data-testid="donor-email" className={inputCls} placeholder={t.donate.donorEmail} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <button
              onClick={donate}
              disabled={loading}
              data-testid="donate-submit"
              className="mt-6 w-full py-5 rounded-full bg-white text-black font-semibold tracking-wide flex items-center justify-center gap-2 hover:bg-purple-500 hover:text-white transition-colors disabled:opacity-60"
            >
              {loading ? (<><Loader2 size={18} className="animate-spin" /> {t.donate.processing}</>) : (<><Heart size={18} /> {t.donate.give}</>)}
            </button>
            <p className="text-center text-xs text-white/40 mt-4 flex items-center justify-center gap-2">
              <Info size={12} /> {t.donate.eligibility}
            </p>
            <p className="text-center text-xs text-white/30 mt-2">CAD · Secured by Stripe</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
